import React, { useState, useEffect } from 'react'
import { Dropdown } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'

import Icon from '../icon'
import { NotificationItem } from '../Notification'
import useApi from '../../hooks/useApi'
import { useCurrentUser } from '../../hooks/useCurrentUser'

export default function NotificationsDropdown() {
  const { t } = useTranslation()
  const {
    getNotifications,
    getUnreadNotificationCount,
    markAllNotificationsAsRead,
    deleteAllNotifications,
  } = useApi()
  const { currentUser } = useCurrentUser()
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [actualUnreadCount, setActualUnreadCount] = useState(0)
  const [displayLimit, setDisplayLimit] = useState(25)
  const [clearingAll, setClearingAll] = useState(false)

  const fetchNotifications = async () => {
    if (!currentUser?.id) {
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await getNotifications()

      if (response.status === 200 && Array.isArray(response.data)) {
        setNotifications(response.data)
        setError(null)
      } else if (
        response.status === 200 &&
        typeof response.data === 'string' &&
        response.data.includes('<!DOCTYPE html>')
      ) {
        setNotifications([]) // Set empty array for no notifications
        setError(null)
      } else {
        console.warn(
          'Notifications API returned unexpected data:',
          response.data,
        )
        setNotifications([])
        setError('Failed to load notifications')
      }
    } catch (err) {
      console.error('Error fetching notifications:', err)
      setError('Failed to load notifications')
      setNotifications([])
    } finally {
      setLoading(false)
    }
  }

  // Fetch unread count
  const fetchUnreadCount = async () => {
    if (!currentUser?.id) {
      return
    }

    try {
      const response = await getUnreadNotificationCount({ id: currentUser.id })

      if (
        response.status === 200 &&
        response.data &&
        typeof response.data === 'object'
      ) {
        setActualUnreadCount(response.data.unread_notifications_count || 0)
      } else if (
        response.status === 200 &&
        typeof response.data === 'string' &&
        response.data.includes('<!DOCTYPE html>')
      ) {
        // Backend returns HTML when user has no notifications
        setActualUnreadCount(0)
      } else {
        console.warn(
          'Unread count API returned unexpected data:',
          response.data,
        )
        setActualUnreadCount(0)
      }
    } catch (err) {
      console.error('Error fetching unread count:', err)
      setActualUnreadCount(0)
    }
  }

  // Fetch unread count on mount for badge display
  useEffect(() => {
    if (!currentUser?.id) return

    // Verify auth headers exist before fetching
    const authHeaders = localStorage.getItem('authHeaders')
    if (!authHeaders) {
      console.warn(
        '⚠️ NotificationsDropdown: No auth headers found, skipping fetch',
      )
      return
    }

    try {
      const parsed = JSON.parse(authHeaders)
      if (!parsed['access-token'] || !parsed.client || !parsed.uid) {
        console.warn(
          '⚠️ NotificationsDropdown: Incomplete auth headers, skipping fetch',
        )
        return
      }
    } catch (e) {
      console.error('⚠️ NotificationsDropdown: Failed to parse auth headers', e)
      return
    }

    // Small delay to ensure auth system is fully initialized
    const timer = setTimeout(() => {
      fetchUnreadCount()
    }, 100)

    return () => clearTimeout(timer)
  }, [currentUser?.id])

  const handleNotificationClick = notificationData => {
    // Since we don't have individual notification read API,
    // we'll just refresh the data to get the latest state
    setDisplayLimit(25) // Reset to show 25 when refreshing
    fetchNotifications()
    fetchUnreadCount()
  }

  const handleActorClick = actor => {
    // TODO: Navigate to user profile, or later event page with later update
    console.log('Navigate to actor profile:', actor)
  }

  const handleMarkAllRead = async () => {
    try {
      const response = await markAllNotificationsAsRead()

      if (response.status === 200) {
        // Refresh data from API to get updated state
        setDisplayLimit(25) // Reset to show 25 when refreshing
        await fetchNotifications()
        await fetchUnreadCount()
      } else {
        console.error('Failed to mark all notifications as read:', response)
      }
    } catch (err) {
      console.error('Error marking all notifications as read:', err)
    }
  }

  const handleClearAll = async () => {
    try {
      setClearingAll(true)
      const response = await deleteAllNotifications()

      if (response.status === 200) {
        // Clear local state and refresh
        setNotifications([])
        setActualUnreadCount(0)
        setDisplayLimit(25)
        setError(null)
      } else {
        console.error('Failed to clear all notifications:', response)
        setError('Failed to clear all notifications')
      }
    } catch (err) {
      console.error('Error clearing all notifications:', err)
      setError('Failed to clear all notifications')
    } finally {
      setClearingAll(false)
    }
  }

  const handleDropdownToggle = isOpen => {
    if (isOpen) {
      setDisplayLimit(25) // Reset to show 25 when opening
      fetchNotifications()
    }
  }

  // Ensure notifications is always an array
  const safeNotifications = Array.isArray(notifications) ? notifications : []
  const unreadNotifications = safeNotifications.filter(n => !n.read_at)
  // Use actualUnreadCount from API instead of props
  const currentUnreadCount = actualUnreadCount

  // Show notifications up to the display limit
  const displayNotifications = safeNotifications.slice(0, displayLimit)

  return (
    <Dropdown
      className="nav-dropdown notifications-dropdown"
      onToggle={handleDropdownToggle}
    >
      <Dropdown.Toggle className="nav-item position-relative notifications-toggle">
        <Icon name="bell" />
        {currentUnreadCount > 0 && (
          <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger notification-badge">
            {currentUnreadCount}
            <span className="visually-hidden">unread notifications</span>
          </span>
        )}
      </Dropdown.Toggle>
      <Dropdown.Menu className="notifications-menu">
        <div className="notifications-header px-3 py-2 border-bottom">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">{t('notifications.title')}</h6>
            {unreadNotifications.length > 0 && (
              <button
                className="btn btn-link btn-sm p-0 text-decoration-none"
                onClick={handleMarkAllRead}
                disabled={loading}
              >
                {t('notifications.mark_all_read')}
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <Dropdown.Item className="text-center text-muted py-3">
            <div
              className="spinner-border spinner-border-sm me-2"
              role="status"
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            Loading notifications...
          </Dropdown.Item>
        ) : error ? (
          <Dropdown.Item className="text-center text-danger py-3">
            {error}
          </Dropdown.Item>
        ) : displayNotifications.length > 0 ? (
          <div
            className="notifications-list"
            style={{ maxHeight: '400px', overflowY: 'auto' }}
          >
            {displayNotifications.map(notification => (
              <Dropdown.Item key={notification.id} className="p-0" as="div">
                <NotificationItem
                  {...notification}
                  onNotificationClick={handleNotificationClick}
                  onActorClick={handleActorClick}
                />
              </Dropdown.Item>
            ))}

            {displayLimit < safeNotifications.length && (
              <>
                <Dropdown.Divider />
                <Dropdown.Item className="p-0" as="div">
                  <button
                    className="btn btn-link btn-sm p-0 text-decoration-none w-100 text-center"
                    onClick={e => {
                      e.preventDefault()
                      e.stopPropagation()
                      setDisplayLimit(
                        Math.min(displayLimit + 25, safeNotifications.length),
                      )
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {t('notifications.load_more')}
                  </button>
                </Dropdown.Item>
              </>
            )}

            {displayLimit >= safeNotifications.length &&
              safeNotifications.length > 0 && (
                <>
                  <Dropdown.Divider />
                  <Dropdown.Item className="p-0" as="div">
                    <button
                      className="btn btn-link btn-sm p-0 text-decoration-none w-100 text-center text-danger"
                      onClick={e => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleClearAll()
                      }}
                      disabled={clearingAll}
                      style={{
                        cursor: clearingAll ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {clearingAll ? (
                        <>
                          <div
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                          >
                            <span className="visually-hidden">Clearing...</span>
                          </div>
                          Clearing...
                        </>
                      ) : (
                        t('notifications.clear_all')
                      )}
                    </button>
                  </Dropdown.Item>
                </>
              )}
          </div>
        ) : (
          <Dropdown.Item className="text-center text-muted">
            {t('notifications.no_notifications')}
          </Dropdown.Item>
        )}
      </Dropdown.Menu>
    </Dropdown>
  )
}

NotificationsDropdown.propTypes = {}
